import { create } from "zustand";
import { DEFAULT_FILTERS, type InboxFilters, type InboxView } from "./inbox-model";

/** Tailwind `xl` breakpoint: the details panel is inline from here up. */
const WIDE_QUERY = "(min-width: 1280px)";
const isWide = () => typeof window !== "undefined" && window.matchMedia(WIDE_QUERY).matches;

export type ComposerMode = "reply" | "note";

interface InboxUiState {
  filters: InboxFilters;
  composerMode: ComposerMode;
  /** Details panel: pinned inline at xl+ (default on); overlay below xl (default off). */
  detailsPinned: boolean;
  detailsOverlay: boolean;
  /** Unsent composer text per conversation id. */
  drafts: Readonly<Record<string, string>>;
  setView: (view: InboxView) => void;
  setQuery: (q: string) => void;
  applyFilters: (patch: Partial<Omit<InboxFilters, "view" | "q">>) => void;
  resetFilters: () => void;
  /** Clears search + filters (keeps the view); bumps `searchKey` so the search box resets. */
  clearAll: () => void;
  searchKey: number;
  setComposerMode: (mode: ComposerMode) => void;
  toggleDetails: () => void;
  closeDetails: () => void;
  setDraft: (conversationId: string, text: string) => void;
}

/** Inbox UI state only (server state lives in TanStack Query). */
export const useInboxStore = create<InboxUiState>((set) => ({
  filters: DEFAULT_FILTERS,
  composerMode: "reply",
  detailsPinned: true,
  detailsOverlay: false,
  drafts: {},
  searchKey: 0,
  setView: (view) => {
    set((s) => ({ filters: { ...s.filters, view } }));
  },
  setQuery: (q) => {
    set((s) => ({ filters: { ...s.filters, q } }));
  },
  applyFilters: (patch) => {
    set((s) => ({ filters: { ...s.filters, ...patch } }));
  },
  resetFilters: () => {
    set((s) => ({ filters: { ...DEFAULT_FILTERS, view: s.filters.view, q: s.filters.q } }));
  },
  clearAll: () => {
    set((s) => ({ filters: { ...DEFAULT_FILTERS, view: s.filters.view }, searchKey: s.searchKey + 1 }));
  },
  setComposerMode: (composerMode) => {
    set({ composerMode });
  },
  toggleDetails: () => {
    set((s) => (isWide() ? { detailsPinned: !s.detailsPinned } : { detailsOverlay: !s.detailsOverlay }));
  },
  closeDetails: () => {
    set(isWide() ? { detailsPinned: false } : { detailsOverlay: false });
  },
  setDraft: (conversationId, text) => {
    set((s) => ({ drafts: { ...s.drafts, [conversationId]: text } }));
  },
}));
